import { useState } from "react";

const SOURCES = [
  {
    id: "it",
    name: "CBS-generated IT Source",
    badge: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
    acc: "text-cyan-300",
    format: "Legacy CBS Excel report",
    adapter: "Row-alignment adapter (no structured Title Code column)",
    how: "Uses row position alignment to locate the corresponding master row, then validates the discovered Title Code and Item Name descriptors against expected values before insertion.",
    outputs: ["E-Banking returns", "E-Commerce returns"],
    note: "The IT source is the only source that does not expose Title Code directly — row alignment is used as the lookup mechanism and then validated.",
  },
  {
    id: "card",
    name: "CARD Department",
    badge: "bg-violet-500/20 text-violet-300 border-violet-500/40",
    acc: "text-violet-300",
    format: "Structured departmental template",
    adapter: "Title Code-first adapter",
    how: "Reads the Title Code column directly as the canonical reporting identity. Maps each row to the exact master row using Title Code lookup. No row alignment required.",
    outputs: ["Card transaction returns"],
    note: "Uses the standard departmental template which the platform manages and distributes.",
  },
  {
    id: "bach",
    name: "BACH Department",
    badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    acc: "text-emerald-300",
    format: "Structured departmental template",
    adapter: "Title Code-first adapter",
    how: "Same Title Code-first extraction as CARD. Each source file is independently validated for reporting period before processing.",
    outputs: ["BACH clearing returns"],
    note: null,
  },
  {
    id: "agent",
    name: "Agent Banking",
    badge: "bg-sky-500/20 text-sky-300 border-sky-500/40",
    acc: "text-sky-300",
    format: "Structured departmental template",
    adapter: "Title Code-first adapter",
    how: "Title Code-based extraction. Agent Banking data is assigned to its own authoritative master zone to prevent conflicts with overlapping IT mirror data.",
    outputs: ["Agent banking returns"],
    note: null,
  },
  {
    id: "banglapay",
    name: "Mobile Banking (Bangla Pay)",
    badge: "bg-teal-500/20 text-teal-300 border-teal-500/40",
    acc: "text-teal-300",
    format: "APP + WEB structured sources",
    adapter: "Split APP/WEB adapter with combined calculation engine",
    how: "APP and WEB are processed as separate sources. The calculation engine derives combined APP+WEB fields from the individual APP and WEB values using the platform's reporting rules.",
    outputs: ["APP returns", "WEB returns", "Combined APP+WEB (calculated)"],
    note: "The APP+WEB combined rows are computed by the platform — not taken directly from either source.",
  },
  {
    id: "remittance",
    name: "Remittance",
    badge: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    acc: "text-amber-300",
    format: "Structured workbook + legacy text source",
    adapter: "Dual-format adapter (XLSX and legacy text)",
    how: "Supports both a structured remittance workbook and a legacy text-based source format. The adapter handles both formats transparently — the processing pipeline receives normalised rows regardless of input format.",
    outputs: ["Remittance returns (structured)", "Remittance returns (legacy)"],
    note: "Legacy text format is maintained for backward compatibility with older source systems.",
  },
];

export default function ETLPipeline() {
  const [active, setActive] = useState("it");
  const source = SOURCES.find((s) => s.id === active);

  return (
    <div className="space-y-4">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500">ETL PIPELINE</p>
        <h4 className="mt-1 text-sm font-semibold text-white">Multi-source ingestion + Title Code extraction</h4>
        <p className="mt-1 text-xs text-slate-400">
          Title Code is the canonical reporting identity for every row. Each source type has a dedicated adapter that normalises its format into Title Code-keyed rows before they enter the master insertion pipeline.
        </p>
      </div>

      <div className="flex gap-3">
        {/* Source selector */}
        <div className="w-40 shrink-0 space-y-0.5">
          <p className="mb-2 font-mono text-[9px] tracking-[0.15em] text-slate-600">DATA SOURCES</p>
          {SOURCES.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              aria-label={`View ${s.name} source details`}
              className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-[11px] transition ${
                active === s.id
                  ? `${s.badge} font-semibold`
                  : "border-transparent text-slate-400 hover:bg-white/5 hover:text-slate-200"
              }`}
            >
              <span className="truncate">{s.name.split(" ")[0]}</span>
            </button>
          ))}
        </div>

        {/* Source detail */}
        {source && (
          <div className="min-w-0 flex-1 space-y-2">
            <div className={`rounded-xl border p-3 ${source.badge}`}>
              <p className="text-sm font-bold">{source.name}</p>
              <p className="text-[10px] mt-0.5">{source.format}</p>
            </div>

            <div className="rounded-lg border border-white/10 bg-black/20 p-3">
              <p className={`mb-1 font-mono text-[9px] tracking-[0.15em] ${source.acc}`}>ADAPTER</p>
              <p className="text-xs font-medium text-slate-200">{source.adapter}</p>
            </div>

            <div className="rounded-lg border border-white/10 bg-black/20 p-3">
              <p className={`mb-1 font-mono text-[9px] tracking-[0.15em] ${source.acc}`}>HOW IT WORKS</p>
              <p className="text-[11px] leading-relaxed text-slate-400">{source.how}</p>
            </div>

            <div className="rounded-lg border border-white/10 bg-black/20 p-3">
              <p className={`mb-2 font-mono text-[9px] tracking-[0.15em] ${source.acc}`}>OUTPUTS</p>
              <div className="flex flex-wrap gap-1.5">
                {source.outputs.map((o) => (
                  <span key={o} className={`rounded border px-2 py-0.5 text-[10px] ${source.badge}`}>{o}</span>
                ))}
              </div>
            </div>

            {source.note && (
              <p className={`rounded-lg border px-2.5 py-2 text-[11px] leading-relaxed ${source.badge}`}>{source.note}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
